import { useCallback, useRef, useState } from 'react';

type Dataset = { x: number; y?: number | null };

type ChartGestureParams = {
  enabled: boolean;
  chartW: number;
  chartH: number;
  padding: { top: number; left: number; bottom: number; right: number };
  /** Extra pixels consumed by VictoryChart's domainPadding on the right. */
  domainPadRight: number;
  domain: { x: [number, number]; y: [number, number] };
  datasets: Dataset[];
};

export type NativeTooltipState = {
  /** Nearest data point — y is always non-null (gaps are skipped during search). */
  datum: { x: number; y: number };
  /** SVG x-pixel of the crosshair. */
  crosshairX: number;
  /** SVG y-pixel of the datum point on the temperature line. */
  crosshairY: number;
};

const findNearest = (p: ChartGestureParams, touchX: number): NativeTooltipState | null => {
  const { chartW, chartH, padding, domainPadRight, domain, datasets } = p;
  const [xMin, xMax] = domain.x;
  const [yMin, yMax] = domain.y;

  const plotW = chartW - padding.left - padding.right - domainPadRight;
  const plotH = chartH - padding.top - padding.bottom;

  const domainX = xMin + ((touchX - padding.left) / plotW) * (xMax - xMin);

  let nearest: Dataset | null = null;
  let minDist = Infinity;
  for (const point of datasets) {
    if (point.y == null) continue;
    const dist = Math.abs(point.x - domainX);
    if (dist < minDist) {
      minDist = dist;
      nearest = point;
    }
  }

  if (!nearest || nearest.y == null) return null;

  const y = nearest.y as number;
  const crosshairX = padding.left + ((nearest.x - xMin) / (xMax - xMin)) * plotW;
  const crosshairY = padding.top + ((yMax - y) / (yMax - yMin)) * plotH;

  return { datum: { x: nearest.x, y }, crosshairX, crosshairY };
};

/**
 * Provides touch handlers and active tooltip state for native chart tooltip
 * detection using React Native's built-in View responder system.
 *
 * Uses `locationX` from the touch event (relative to the overlay View) so no
 * extra coordinate math is needed. On web this is a no-op — VictoryVoronoiContainer
 * handles it.
 */
export const useChartGesture = (params: ChartGestureParams) => {
  const [tooltip, setTooltip] = useState<NativeTooltipState | null>(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const handleTouchMove = useCallback((locationX: number) => {
    const p = paramsRef.current;
    if (!p.enabled) return;
    setTooltip(findNearest(p, locationX));
  }, []);

  const handleTouchEnd = useCallback(() => {
    setTooltip(null);
  }, []);

  return { handleTouchMove, handleTouchEnd, tooltip };
};
